<template>
  <div class="ad-banner-config-container">
    <div class="page-header">
      <div>
        <h2>广告栏配置</h2>
        <p>配置前台首页顶部广告栏标题与跳转链接</p>
      </div>
    </div>

    <el-row :gutter="20">
      <el-col :xs="24" :lg="14">
        <el-card class="box-card config-card" shadow="hover">
          <template #header>
            <div class="card-header">
              <span class="card-title">配置内容</span>
              <el-button
                :icon="Refresh"
                :loading="loading"
                @click="fetchConfig"
              >
                刷新
              </el-button>
            </div>
          </template>

          <el-form
            ref="formRef"
            v-loading="loading"
            :model="form"
            :rules="rules"
            label-width="96px"
          >
            <el-form-item label="启用状态" prop="enabled">
              <el-switch
                v-model="form.enabled"
                active-text="启用"
                inactive-text="停用"
              />
            </el-form-item>

            <el-form-item label="广告标题" prop="title">
              <el-input
                v-model="form.title"
                maxlength="80"
                show-word-limit
                placeholder="请输入广告栏标题"
              />
            </el-form-item>

            <el-form-item label="跳转链接" prop="linkUrl">
              <el-input
                v-model="form.linkUrl"
                placeholder="请输入 http 或 https 开头的跳转链接"
              >
                <template #prefix>
                  <el-icon><Link /></el-icon>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item>
              <el-button
                type="primary"
                :icon="Check"
                :loading="saving"
                @click="handleSubmit"
              >
                保存配置
              </el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>

      <el-col :xs="24" :lg="10">
        <el-card class="box-card preview-card" shadow="hover">
          <template #header>
            <div class="card-header">
              <span class="card-title">前台预览</span>
              <el-tag :type="form.enabled ? 'success' : 'info'">
                {{ form.enabled ? '展示中' : '已停用' }}
              </el-tag>
            </div>
          </template>

          <div
            class="banner-preview"
            :class="{ disabled: !form.enabled }"
            @click="handlePreviewClick"
          >
            <span class="preview-dot"></span>
            <span class="preview-title">
              {{ form.title || '请输入广告栏标题' }}
            </span>
            <el-icon class="preview-link-icon"><Link /></el-icon>
          </div>

          <div class="preview-detail">
            <div class="detail-label">跳转链接</div>
            <div class="detail-value">{{ form.linkUrl || '-' }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { Check, Link, Refresh } from '@element-plus/icons-vue'
import { getAdBannerConfig, updateAdBannerConfig } from '@/api/ad-banner'

const defaultConfig = {
  title: '就业热点：5 月的招聘市场，Agent 全栈，面试八股化严重',
  linkUrl: 'https://mp.weixin.qq.com/s/1Ml-nH2BVv_Cp9wUleF8Tw',
  enabled: true
}

const formRef = ref(null)
const loading = ref(false)
const saving = ref(false)
const form = reactive({ ...defaultConfig })

const validateUrl = (_rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入跳转链接'))
    return
  }

  if (!/^https?:\/\/.+/i.test(value)) {
    callback(new Error('跳转链接必须以 http:// 或 https:// 开头'))
    return
  }

  callback()
}

const rules = {
  title: [
    { required: true, message: '请输入广告标题', trigger: 'blur' },
    { max: 80, message: '标题不能超过80个字符', trigger: 'blur' }
  ],
  linkUrl: [{ required: true, validator: validateUrl, trigger: 'blur' }],
  enabled: [{ required: true, message: '请选择启用状态', trigger: 'change' }]
}

const applyConfig = (config = {}) => {
  form.title = config.title || defaultConfig.title
  form.linkUrl = config.linkUrl || defaultConfig.linkUrl
  form.enabled =
    typeof config.enabled === 'boolean' ? config.enabled : defaultConfig.enabled
}

const fetchConfig = async () => {
  loading.value = true
  try {
    const res = await getAdBannerConfig()
    applyConfig(res)
  } catch (error) {
    console.error('获取广告栏配置失败:', error)
    ElMessage.error('获取广告栏配置失败')
  } finally {
    loading.value = false
  }
}

const handleSubmit = () => {
  if (!formRef.value) return

  formRef.value.validate(async (valid) => {
    if (!valid) return

    saving.value = true
    try {
      const res = await updateAdBannerConfig({
        title: form.title,
        linkUrl: form.linkUrl,
        enabled: form.enabled
      })
      applyConfig(res)
      ElMessage.success('广告栏配置已保存')
    } catch (error) {
      console.error('保存广告栏配置失败:', error)
      ElMessage.error('保存广告栏配置失败')
    } finally {
      saving.value = false
    }
  })
}

const handlePreviewClick = () => {
  if (!form.enabled || !/^https?:\/\//i.test(form.linkUrl)) return
  window.open(form.linkUrl, '_blank', 'noopener,noreferrer')
}

onMounted(() => {
  fetchConfig()
})
</script>

<style scoped lang="scss">
.ad-banner-config-container {
  min-height: calc(100vh - var(--admin-header-height) - var(--admin-tags-height));
  color: var(--admin-color-text);

  .config-card,
  .preview-card {
    margin-bottom: 20px;
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .card-title {
    color: var(--admin-color-text);
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
  }

  .banner-preview {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 10px 16px;
    border-radius: 8px;
    color: #ffffff;
    background: linear-gradient(90deg, #059669 0%, #14b8a6 52%, #0ea5e9 100%);
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(15, 118, 110, 0.18);
    transition: opacity 0.18s ease;

    &.disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
  }

  .preview-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ffffff;
    flex: 0 0 8px;
  }

  .preview-title {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
  }

  .preview-link-icon {
    flex: 0 0 auto;
  }

  .preview-detail {
    margin-top: 16px;
    padding: 14px;
    border: 1px solid var(--admin-color-border-soft);
    border-radius: 8px;
    background: #f8fafc;
  }

  .detail-label {
    margin-bottom: 6px;
    color: var(--admin-color-text-muted);
    font-size: 13px;
    line-height: 18px;
  }

  .detail-value {
    color: var(--admin-color-text-regular);
    font-size: 13px;
    line-height: 20px;
    word-break: break-all;
  }
}
</style>
